import React, { useMemo, useRef } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';
import {
  getArrayLength,
  getHasElementsInArray,
  getIsNodeFolder,
  getNodeMimeType,
} from 'utils';
import MymeTypeIcon from '../../../../../MymeTypeIcon';
import useCallbackAsync from 'hooks/useCallbackAsync';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import { Button } from 'ncoded-component-library';
import FolderPlusIcon from 'icons/FolderPlus.icon';
import PopNotificationWithUndo from 'components/PopNotificationWithUndo';
import useUndoActions from 'hooks/useUndoActions';
import { ShareDataRoomNodes } from 'models/ShareableLink';

import './FileInfoInstance.styles.scss';

export type FileInfoInstanceProps = {
  className?: string;
  linkId: string;
  checkedFolderIds: string[];
  checkedFileIds: string[];
  node: DataRoomNode;
  updateSecureLinkNodes?: (link: ShareDataRoomNodes) => void;
};

const FileInfoInstance: React.FC<FileInfoInstanceProps> = (props) => {
  const {
    className,
    node,
    linkId,
    checkedFileIds,
    checkedFolderIds,
    updateSecureLinkNodes,
  } = props;

  const { name, childrenIds, ...rest } = node;

  const classes = classNames('file-info-instance', className);

  const { t } = useTranslation();

  const isFolder = getIsNodeFolder(node);

  const childrenLength = getArrayLength(childrenIds);

  const { isUndoAvailable, setUndoTimeout, clearUndoTimeout } =
    useUndoActions();

  const newFiles = useMemo(
    () => childrenIds.filter((id) => !checkedFileIds.includes(id)),
    [checkedFileIds, childrenIds],
  );

  const newFilesLength = useMemo(() => getArrayLength(newFiles), [newFiles]);

  const newFilesRef = useRef(newFilesLength);

  const hasNewFiles = useMemo(
    () => getHasElementsInArray(newFiles),
    [newFiles],
  );

  const [updateShareAll, isLoading] = useCallbackAsync(async () => {
    if (!linkId || !hasNewFiles) return;

    const payload = {
      folderIds: checkedFolderIds,
      fileIds: [...checkedFileIds, ...newFiles],
    };

    clearUndoTimeout();

    await dataRoomApi.shareableLinks.shareAllFiles(linkId, payload);

    setUndoTimeout();
    updateSecureLinkNodes(payload);
  });

  const [onUndoShareAll, isLoadingUndo] = useCallbackAsync(async () => {
    if (!linkId) return;

    clearUndoTimeout();

    const { data } = await dataRoomApi.shareableLinks.undoShareAllFiles(linkId);

    updateSecureLinkNodes(data);
  });

  return (
    <>
      <li className={classes}>
        <div className="file-info-instance__name">
          <MymeTypeIcon mimeType={getNodeMimeType(rest)} />
          <span>{name}</span>
        </div>

        {isFolder && (
          <span>
            {t(hasNewFiles ? 'numberOfFilesPart' : 'numberOfFile', {
              part: childrenLength - newFilesLength || 0,
              count: childrenLength,
            })}
          </span>
        )}

        {hasNewFiles && (
          <>
            <span className="new-files">{t('partiallyShared')}</span>
            <Button
              variant="link"
              disabled={isLoading}
              icon={FolderPlusIcon}
              onClick={updateShareAll}
            >
              {t('shareAll')}
            </Button>
          </>
        )}
      </li>

      <PopNotificationWithUndo
        isUndoAvailable={isUndoAvailable}
        disabled={isLoadingUndo}
        onUndo={onUndoShareAll}
      >
        <p>
          {t('allNewFilesShared', {
            name,
            count: newFilesRef?.current,
          })}
        </p>
      </PopNotificationWithUndo>
    </>
  );
};

export default FileInfoInstance;
